import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { formatMoney } from "../lib/settings";

import { tr } from "../lib/i18n";
const Row = ({ label, value, testid, strong }) => (
  <tr style={{ borderBottom: "1px solid var(--divider-soft)" }}>
    <td className="py-2 text-sm opacity-70">{label}</td>
    <td className={`py-2 text-sm tabular text-end ${strong ? "font-semibold" : ""}`} data-testid={testid}>{value}</td>
  </tr>
);

/** Consignment settlement: commission, deductions, net payable to the owner, payouts. */
export const ConsignmentPanel = ({ vehicleId, settings }) => {
  const { can, user } = useAuth();
  const nav = useNavigate();
  const [a, setA] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/consignment", { params: { vehicle_id: vehicleId } });
      const first = (data.items || [])[0];
      setA(first ? (await api.get(`/consignment/${first.id}`)).data : null);
    } catch { setA(null); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vehicleId]);
  if (!a) return null;

  const money = (v) => (v == null ? "—" : formatMoney(v, settings));
  const sold = a.net_payable != null;
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/consignment/${a.id}/finalize-payout`, amount ? { amount: Number(amount) } : {});
      toast.success(tr("x.payout_recorded", { p0: data.payout_status }));
      setAmount(""); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="mt-6" data-testid="consignment-panel">
      <div className="kicker mb-2">{tr("x.consignment_settlement")}</div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          <Row label={tr("x.commission")} testid="consign-commission-rule" value={a.commission_value == null
            ? <span className="text-red-600 font-semibold" data-testid="consign-commission-not-set">{tr("x.commission_not_set")}</span>
            : a.commission_type === "Percentage" ? tr("x.pct_of_selling_price", { p0: a.commission_value }) : tr("x.fixed_amount", { p0: money(a.commission_value) })} />
          {sold && <Row label={tr("x.commission_amount")} value={money(a.commission_amount)} testid="consign-commission" />}
          {sold && <Row label={tr("x.deductions")} value={money(a.deductions_total || 0)} testid="consign-deductions" />}
          {sold && <Row label={tr("x.net_payable_to_owner")} value={money(a.net_payable)} testid="consign-net-payable" strong />}
          {sold && a.paid_to_owner != null && <Row label={tr("x.paid_to_owner")} value={money(a.paid_to_owner)} testid="consign-paid" />}
          {sold && a.balance != null && <Row label={tr("x.balance_due_to_owner")} value={money(a.balance)} testid="consign-balance" strong />}
          <Row label={tr("x.payout_status")} value={sold ? a.payout_status : tr("x.not_sold_yet")} testid="consign-payout-status" />
        </tbody>
      </table>
      {a.commission_value == null && ["admin", "management"].includes(user?.role) && a.source_form_id && (
        <button className="btn-primary mt-3" onClick={() => nav(`/forms/sf/${a.source_form_id}`)} data-testid="consign-set-commission">{tr("x.set_commission_now")}</button>
      )}
      {sold && a.payout_status !== "Paid" && can("consignment", "approve") && can("payments", "create") && (
        <div className="flex gap-2 mt-3">
          <input type="number" min="0" step="0.001" placeholder={tr("x.amount_blank_full_balance")} value={amount} onChange={(e) => setAmount(e.target.value)}
            className="h-9 rounded-lg border border-[hsl(var(--border))] px-3 text-sm flex-1" data-testid="consign-payout-amount" />
          <button className="btn-primary" disabled={busy} onClick={pay} data-testid="consign-payout-btn">{tr("x.pay_owner")}</button>
        </div>
      )}
    </div>
  );
};
